import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import * as d3 from 'd3';

const ParkingEntityDrawer = ({ entityDetails, chartRef }) => {
  // Mock parking data
  const parkingData = {
    cameras: [
      {
        id: 'PARK-CAM-001',
        location: 'Main Parking Lot A',
        status: 'Active',
        lastDetection: '2024-01-15T10:30:00Z',
        detections: {
          vehicles: 45,
          people: 12,
          alerts: 3
        }
      },
      {
        id: 'PARK-CAM-002', 
        location: 'Parking Lot B',
        status: 'Active',
        lastDetection: '2024-01-15T10:28:00Z',
        detections: {
          vehicles: 32,
          people: 8,
          alerts: 1
        }
      }
    ],
    totalDetections: {
      vehicles: 77,
      people: 20,
      alerts: 4
    },
    capacityData: [
      { category: 'Available', value: 65, color: '#4CAF50' },
      { category: 'Occupied', value: 25, color: '#FF9800' },
      { category: 'Reserved', value: 8, color: '#2196F3' },
      { category: 'Maintenance', value: 2, color: '#F44336' }
    ]
  };

  useEffect(() => {
    if (chartRef.current && parkingData.capacityData.length > 0) {
      renderParkingCapacityChart();
    }
  }, [parkingData]);

  const renderParkingCapacityChart = () => {
    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 300;
    const height = 300;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    const arcHover = d3.arc()
      .innerRadius(0)
      .outerRadius(radius + 10);

    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    const path = svg.selectAll('path')
      .data(pie(parkingData.capacityData))
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', 'white')
      .style('stroke-width', '2px')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arcHover);
        
        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        const total = parkingData.capacityData.reduce((sum, item) => sum + item.value, 0);
        const percentage = ((d.data.value / total) * 100).toFixed(1);
        
        tooltip.html(`
          <strong>${d.data.category}</strong><br/>
          ${d.data.value} spots (${percentage}%)
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc);
        
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });

    // Add labels
    const label = svg.selectAll('text')
      .data(pie(parkingData.capacityData))
      .enter()
      .append('text')
      .text(d => d.data.category)
      .attr('transform', d => `translate(${arc.centroid(d)})`)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', 'white');

    // Add center text
    const total = parkingData.capacityData.reduce((sum, item) => sum + item.value, 0);
    svg.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(`${total} Total`);
  };

  return (
    <>
      {/* Parking Cameras Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <CameraAltIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Parking Cameras
            </Typography>
          </Box>
          
          {/* Camera List */}
          <List dense>
            {parkingData.cameras.map((camera, index) => (
              <ListItem key={camera.id} divider>
                <ListItemIcon>
                  <CameraAltIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {camera.id}
                      </Typography>
                      <Chip 
                        label={camera.status} 
                        size="small" 
                        color={camera.status === 'Active' ? 'success' : 'error'}
                        variant="outlined" 
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Location: {camera.location}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Last Detection: {new Date(camera.lastDetection).toLocaleString()}
                      </Typography>
                      <Box display="flex" gap={2} sx={{ mt: 1 }}>
                        <Chip 
                          label={`${camera.detections.vehicles} vehicles`} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${camera.detections.people} people`} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${camera.detections.alerts} alerts`} 
                          size="small" 
                          color="warning"
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Total Detections Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Total Detections (Last 24h)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {parkingData.totalDetections.vehicles}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Vehicles
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {parkingData.totalDetections.people}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    People
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="warning.main">
                    {parkingData.totalDetections.alerts}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Alerts
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Parking Capacity Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <LocalParkingIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Parking Capacity
            </Typography>
          </Box>
          <Box 
            ref={chartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default ParkingEntityDrawer; 